import { getMonth, getYear, subMonths } from 'date-fns';
import format from 'date-fns/format';
import styles from 'views/Landing/Landing.module.scss';

/**
 * Format a number to have commas at the thousanth place.
 * @param {number} number Number to format
 * @param {boolean} isStats whether number to format is for stats bar, defaulted to false
 * @returns {string} Formatted number converted to string with commas
 */
export const numberFormat = (number, isStats = false) => {
    const formatForStats = new Intl.NumberFormat().format(number);
    return isStats
        ? formatForStats
        : new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          }).format(number);
};

/**
 * Format a number to have K for thousandth place, M for millionth place or B for billionth place, with 2 DP.
 * @param {number} number Number to format
 * @returns {string} Formatted number converted to string with either M,B or K with 2 DP
 */
export const formatNumberToKMB = (number) => {
    if (number >= 1000000000 || number <= -1000000000)
        return (number / 1000000000).toFixed(2) + 'B';
    if (number >= 1000000 || number <= -1000000)
        return (number / 1000000).toFixed(2) + 'M';
    if (number >= 1000 || number <= -1000)
        return (number / 1000).toFixed(2) + 'k';
    return numberFormat(parseFloat(number.toFixed(2)));
};

export const calculateLeftValueForGraphTooltip = (barValue, highestScore) =>
    10 + (barValue / highestScore) * 15;

export const calculateTopValueForGraphTooltip = (
    numberOfBar,
    chartsContainerHeight,
    selectedDataIndex
) => {
    const middleOfContainerHeight = chartsContainerHeight / 2;

    if (numberOfBar === 1) {
        return middleOfContainerHeight + 15;
    }
    if (numberOfBar === 2) {
        if (selectedDataIndex === 0) return middleOfContainerHeight + 120;
        if (selectedDataIndex === 1) return middleOfContainerHeight - 92;
    }
    if (numberOfBar === 3) {
        if (selectedDataIndex === 0) return middleOfContainerHeight + 120;
        if (selectedDataIndex === 1) return middleOfContainerHeight + 15;
        if (selectedDataIndex === 2) return middleOfContainerHeight - 90;
    }
    if (numberOfBar === 4) {
        if (selectedDataIndex === 0) return middleOfContainerHeight + 120;
        if (selectedDataIndex === 1) return middleOfContainerHeight + 55;
        if (selectedDataIndex === 2) return middleOfContainerHeight - 20;
        if (selectedDataIndex === 3) return middleOfContainerHeight - 90;
    }
    if (numberOfBar === 5) {
        if (selectedDataIndex === 0) return middleOfContainerHeight + 120;
        if (selectedDataIndex === 1) return middleOfContainerHeight + 70;
        if (selectedDataIndex === 2) return middleOfContainerHeight + 15;
        if (selectedDataIndex === 3) return middleOfContainerHeight - 40;
        if (selectedDataIndex === 4) return middleOfContainerHeight - 90;
        return 0;
    }
};

export const calculateTopValueForLandingGraphTooltip = (
    numberOfBar,
    middleOfContainerHeight,
    selectedDataIndex
) => {
    if (numberOfBar < 1) return 0;
    if (numberOfBar === 1) return middleOfContainerHeight + 15;
    if (numberOfBar === 2) {
        if (selectedDataIndex === 0) return middleOfContainerHeight + 70;
        if (selectedDataIndex === 1) return middleOfContainerHeight - 40;
    }
    if (numberOfBar > 2) {
        if (selectedDataIndex === 0) return middleOfContainerHeight + 70;
        if (selectedDataIndex === 1) return middleOfContainerHeight + 15;
        if (selectedDataIndex === 2) return middleOfContainerHeight - 40;
    }
};

/**
 * Check if a value is null and if it is return 0, if not return the same value.
 * @param {string|number} value String or Number to check
 * @returns {string|number} value that was passed in or 0
 */
export const checkNullReturnZero = (value) => (value === null ? 0 : value);

/**
 * Check if a value is a number or not.
 * @param {Number} num Number to check
 * @returns {Boolean} if is number return true, if not false
 */
export const isTypeNumber = (num) => typeof num === 'number';

export const isNumberSingleDigit = (num) => /^\d$/.test(num);

/**
 * Calculate start and end month to be displayed based on whether it's the same month or not, and whether it is
 * for emerging spike rms or not
 * @param {epoch} tsStart start date epoch
 * @param {epoch} tsEnd end date epoch
 * @param {boolean} isSameMonth boolean to check whether start and end month is the same
 * @param {boolean} isNonSpike boolean to if calculation should be for emerging spike or non spike rms
 * @returns {Object} startEndObject
 * @returns {Object} startEndObject.start
 * @returns {String} startEndObject.start.month
 * @returns {Number} startEndObject.start.monthNum
 * @returns {String} startEndObject.start.monthText
 * @returns {String} startEndObject.start.year
 * @returns {Object} startEndObject.end
 * @returns {String} startEndObject.end.month
 * @returns {Number} startEndObject.end.monthNum
 * @returns {String} startEndObject.end.monthText
 * @returns {String} startEndObject.end.year
 */
export const calculateStartEndMonth = (
    tsStart,
    tsEnd,
    isSameMonth,
    isNonSpike = false
) => {
    if (!tsStart || !tsEnd) return;
    if (!isSameMonth) {
        const newEndDate = subMonths(
            new Date(parseInt(tsEnd.year), tsEnd.monthNum - 1, 1),
            1
        );
        const newEndMonth = getMonth(newEndDate);
        const newEndMonthText = format(newEndDate, 'MMM');
        const newEndYear = getYear(newEndDate);

        const finalNewEndMonth = newEndMonth + 1;
        return {
            start: tsStart,
            end: {
                ...tsEnd,
                month: isNumberSingleDigit(finalNewEndMonth)
                    ? `0${finalNewEndMonth}`
                    : `${finalNewEndMonth}`,
                monthNum: finalNewEndMonth,
                monthText: newEndMonthText,
                year: `${newEndYear}`
            }
        };
    }
    const newStartDate = subMonths(
        new Date(
            parseInt(tsStart.year),
            isNonSpike ? tsStart.monthNum : tsStart.monthNum - 1,
            1
        ),
        1
    );
    const newStartMonth = getMonth(newStartDate);
    const newStartMonthText = format(newStartDate, 'MMM');
    const newStartYear = getYear(newStartDate);

    const finalNewStartMonth = isNonSpike ? newStartMonth : newStartMonth + 1;

    return {
        start: {
            ...tsStart,
            month: isNumberSingleDigit(finalNewStartMonth)
                ? `0${finalNewStartMonth}`
                : `${finalNewStartMonth}`,
            monthNum: finalNewStartMonth,
            monthText: newStartMonthText,
            year: `${newStartYear}`
        },
        end: tsStart
    };
};

/**
 * Generate dynamic date range to be displayed at RM Cards Group top right hand corner
 * @param {Object} payload payload object
 * @param {epoch} payload.tsStart start date epoch
 * @param {epoch} payload.tsEnd end date epoch
 * @param {epoch} payload.nonSpikeStart start date epoch
 * @param {epoch} payload.nonSpikeEnd end date epoch
 * @param {epoch} payload.start start date epoch
 * @param {epoch} payload.end end date epoch
 * @param {boolean} payload.isSpike boolean to check whether is emerging spike or non-spike rm
 * @param {boolean} payload.shouldShowSameWhenOneMonth boolean to control if should show same month when difference
 * is only one month
 * @returns {JSX.Element} The new JSX element to be displayed at the upper right of the RM Card Group
 */
export const generateUpperRightDateRangeContent = (payload) => {
    const {
        tsStart,
        tsEnd,
        nonSpikeStart,
        nonSpikeEnd,
        start,
        end,
        isSpike = false,
        shouldShowSameWhenOneMonth = false
    } = { ...payload };
    return (
        tsStart &&
        tsEnd && (
            <div className={styles.highestSpikeUpperRightContent}>
                <div>
                    <p>{isSpike ? 'As at' : 'From'}</p>
                </div>
                <div>
                    {shouldShowSameWhenOneMonth ? (
                        <p>
                            {nonSpikeStart && nonSpikeStart.monthText}{' '}
                            {nonSpikeStart && nonSpikeStart.year}
                        </p>
                    ) : (
                        <p>
                            {start && start.monthText} {start && start.year}
                        </p>
                    )}
                </div>
                <div>
                    <p>{isSpike ? 'vs' : 'to'}</p>
                </div>
                <div>
                    {shouldShowSameWhenOneMonth ? (
                        <p>
                            {nonSpikeEnd && nonSpikeEnd.monthText}{' '}
                            {nonSpikeEnd && nonSpikeEnd.year}
                        </p>
                    ) : (
                        <p>
                            {end && end.monthText} {end && end.year}
                        </p>
                    )}
                </div>
            </div>
        )
    );
};

/**
 * Check if a number value is 0 or 100, with or without decimal places.
 * If it is, return it without decimal places, and if it is not, return it in 2 decimal places.
 * @param {Number} num Number to check
 * @returns {string|Number} either 0 or 100, or other numbers to 2 decimal places
 */
export const formatNumber2DPStringIfNotZeroOrHundred = (num) => {
    if (num === null) return '0%';
    if (isNaN(num) || num === Math.LN2 || num === Infinity) return num;
    if (typeof num !== 'number') return num;
    if (num > 0 && num < 1) return `${num.toFixed(2)}%`;

    // remove any decimal places if any
    const numberRemoveDecimalPlaces = num | 0;

    if (numberRemoveDecimalPlaces === 0) return '0%';
    if (numberRemoveDecimalPlaces === 100) return '100%';

    return `${num.toFixed(2)}%`;
};

/**
 * Check if a value is a number
 * A valid number is can be positive, negative, 0 or a Double
 * @param value value to be checked
 * @returns {boolean} If the value is a number by the above definition, return true, if not, return false
 */
export const isNumber = (value) => {
    if (isNaN(value) || value === Infinity || value === Math.LN2) return false;
    return typeof value === 'number';
};
